import { BarChart3, FileUp, Plus } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import type { Sale } from "@/components/atomic-crm/types";

import { useAjustes } from "../datos/useAjustes";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useCon36 } from "../datos/useFiltroEtapas";
import { useMiRol } from "../datos/useMiRol";
import { CampanaAvisos } from "../equipo/CampanaAvisos";
import { ComisionesDelMes } from "./ComisionesDelMes";
import { agruparAgenda, elementosAgenda } from "../dominio/agenda";
import { claveOrigen, ORIGENES } from "../dominio/catalogos";
import {
  claveEtapa,
  columnasTablero,
  esAparcada,
  etapasDe,
} from "../dominio/etapas";
import { esLeadPendiente } from "../dominio/panel";
import { semaforo } from "../dominio/semaforo";
import type { Negocio } from "../dominio/tipos";
import { EnlacePerfilMovil } from "../layout/EnlacePerfilMovil";
import { MarcaCabecera } from "../layout/MarcaCabecera";
import { EtiquetaEtapa } from "../negocios/EtiquetaEtapa";
import {
  formatoFecha,
  formatoFechaHora,
  formatoImporte,
} from "../negocios/formato";
import { Semaforo } from "../negocios/Semaforo";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { ResumenPage } from "../resumen/ResumenPage";
import { useResumenDisponible } from "../resumen/useResumenDisponible";
import type { Nota } from "../dominio/tipos";

/** Etapas de Ventas que cuenta el Inicio (sin Cobrado: pasa sola a Clientes). */
const ETAPAS_VENTAS_INICIO = etapasDe("ventas").filter((e) => e !== "cobrado");
/** Columnas del tablero de Ventas: su valor es el «valor en el tablero». */
const TABLERO_VENTAS = columnasTablero("ventas");

/** Panel de dirección (SPEC.md §6): pantalla de inicio. Desde S8 cuenta proyectos. */
export const PanelDireccion = () => {
  const translate = useTranslate();
  const resumenDisponible = useResumenDisponible();
  const isMobile = useIsMobile();
  const esAdmin = useEsAdmin();
  const { esConsultor, vende } = useMiRol();
  const con36 = useCon36();
  const { identity } = useGetIdentity();
  const { ajustes } = useAjustes();
  const { data: proyectos, isPending } = useGetList<Negocio>(
    "proyectos_vista",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      // S10b: sin los negocios que Prospect descubre y no entrega (miles desde el 17/10).
      filter: { en_leads: true },
    },
  );
  const { data: usuarios } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const abrirFicha = useAbrirFicha();
  // Motivos de «No le interesa» (02/10/2026): qué objeción se repite.
  const { data: notasNoInteresa } = useGetList<Nota>("notas", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "fecha", order: "DESC" },
    filter: { resultado: "no_interesa" },
  });
  const motivos = useMemo(() => {
    const cuenta = new Map<string, number>();
    for (const nota of notasNoInteresa ?? []) {
      const clave = nota.objecion?.trim() || "";
      cuenta.set(clave, (cuenta.get(clave) ?? 0) + 1);
    }
    return [...cuenta.entries()].sort((a, b) => b[1] - a[1]);
  }, [notasNoInteresa]);

  const resumen = useMemo(() => {
    const lista = proyectos ?? [];
    const porEtapa = Object.fromEntries(
      ETAPAS_VENTAS_INICIO.map((e) => [e, 0]),
    ) as Record<string, number>;
    const porOrigen = Object.fromEntries(ORIGENES.map((o) => [o, 0])) as Record<
      string,
      number
    >;
    const porAsignado = new Map<string, number>();
    const negocios = new Set<string>();
    let valorTablero = 0;
    let clientes = 0;
    let reuniones = 0;
    // Mismo criterio que la lista de Leads (dominio/panel.ts): Lead nuevo sin «no volver
    // a contactar».
    let leadsPendientes = 0;
    for (const n of lista) {
      if (n.embudo === "ventas") {
        porEtapa[n.etapa] = (porEtapa[n.etapa] ?? 0) + 1;
      } else if (!esAparcada(n.etapa)) {
        clientes += 1;
      }
      if (esLeadPendiente(n)) leadsPendientes += 1;
      if (
        n.etapa === "consultoria_agendada" ||
        n.etapa === "presentacion_agendada"
      ) {
        reuniones += 1;
      }
      if (TABLERO_VENTAS.includes(n.etapa)) {
        valorTablero += n.valor_estimado ?? 0;
      }
      // Origen y responsable son del negocio: se cuentan una vez por negocio.
      const negocio = String(n.negocio_id);
      if (!negocios.has(negocio)) {
        negocios.add(negocio);
        porOrigen[n.origen] = (porOrigen[n.origen] ?? 0) + 1;
        const clave = n.asignado_a == null ? "" : String(n.asignado_a);
        porAsignado.set(clave, (porAsignado.get(clave) ?? 0) + 1);
      }
    }
    const agenda = agruparAgenda(elementosAgenda(lista));
    const enRojo = lista
      .filter((n) => semaforo(n, ajustes.semaforo) === "rojo")
      .sort((a, b) =>
        (a.ultimo_contacto ?? a.created_at).localeCompare(
          b.ultimo_contacto ?? b.created_at,
        ),
      );
    return {
      leadsPendientes,
      porEtapa,
      clientes,
      reuniones,
      porOrigen,
      porAsignado,
      valorTablero,
      agenda,
      enRojo,
      total: negocios.size,
    };
  }, [proyectos, ajustes]);

  const nombreUsuario = (id: string) => {
    if (!id) return translate("nevora.negocios.sin_asignar");
    const u = usuarios?.find((s) => String(s.id) === id);
    return u ? `${u.first_name} ${u.last_name}` : id;
  };

  const cuerpo = (
    <div className="space-y-6 pb-8">
      <div>
        <p className="etiqueta mb-1">
          {translate(
            esAdmin
              ? "nevora.panel_direccion.titulo"
              : "nevora.panel_direccion.titulo_setter",
          )}
        </p>
        <h1 className="titulo-pagina">
          {translate("nevora.panel.hola", { nombre: identity?.fullName ?? "" })}
        </h1>
        {resumenDisponible && !esConsultor ? (
          // 28d: en el móvil no hay sitio en la barra de abajo; desde aquí se llega. 39: el
          // consultor no llama, así que no tiene resumen de llamadas.
          <Link
            to={ResumenPage.path}
            className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            <BarChart3 className="size-4" aria-hidden />
            {translate("nevora.resumen.titulo")}
          </Link>
        ) : null}
      </div>

      {!isPending && resumen.total === 0 ? (
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <p>{translate("nevora.panel_direccion.sin_fichas")}</p>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/negocios/create">
                <Plus className="size-4 mr-1" />
                {translate("nevora.negocios.nueva_ficha")}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/importar">
                <FileUp className="size-4 mr-1" />
                {translate("nevora.importar.titulo")}
              </Link>
            </Button>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Cifra
          etiqueta={translate("nevora.panel_direccion.leads_pendientes")}
          valor={String(resumen.leadsPendientes)}
          enlace="/negocios"
        />
        <Cifra
          etiqueta={translate("nevora.panel_direccion.reuniones")}
          valor={String(resumen.reuniones)}
          enlace="/pipeline"
        />
        {esAdmin ? (
          // 36: sin importes para el setter (los suyos, en «Mis comisiones»).
          <Cifra
            etiqueta={translate("nevora.panel_direccion.valor_pipeline")}
            valor={formatoImporte(resumen.valorTablero) || "0 €"}
            enlace="/pipeline"
          />
        ) : null}
        <Cifra
          etiqueta={translate("nevora.panel_direccion.en_rojo")}
          valor={String(resumen.enRojo.length)}
          alerta={resumen.enRojo.length > 0}
        />
      </div>

      {/* 40: setters y consultores ven aquí sus comisiones del mes. */}
      {!esAdmin && (vende || esConsultor) && con36 ? (
        <ComisionesDelMes />
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tarjeta titulo={translate("nevora.panel_direccion.por_etapa")}>
          <Barras
            filas={[
              ...ETAPAS_VENTAS_INICIO.map((e) => ({
                etiqueta: translate(claveEtapa(e)),
                valor: resumen.porEtapa[e] ?? 0,
              })),
              {
                etiqueta: translate("nevora.panel_direccion.clientes"),
                valor: resumen.clientes,
              },
            ]}
          />
        </Tarjeta>
        <Tarjeta titulo={translate("nevora.panel_direccion.por_origen")}>
          <Barras
            filas={ORIGENES.map((o) => ({
              etiqueta: translate(claveOrigen(o)),
              valor: resumen.porOrigen[o] ?? 0,
            }))}
          />
        </Tarjeta>
        {/* Un setter solo ve lo suyo: sin reparto por persona (Robert, 09/10/2026). */}
        {esAdmin ? (
          <Tarjeta titulo={translate("nevora.panel_direccion.por_asignado")}>
            <Barras
              filas={[...resumen.porAsignado.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([id, v]) => ({ etiqueta: nombreUsuario(id), valor: v }))}
            />
          </Tarjeta>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tarjeta titulo={translate("nevora.panel_direccion.hoy")}>
          <ListaAgenda
            elementos={[...resumen.agenda.vencidos, ...resumen.agenda.hoy]}
            vacio={translate("nevora.panel_direccion.nada_hoy")}
          />
        </Tarjeta>
        <Tarjeta titulo={translate("nevora.panel_direccion.semana")}>
          <ListaAgenda
            elementos={[...resumen.agenda.manana, ...resumen.agenda.semana]}
            vacio={translate("nevora.panel_direccion.nada_semana")}
          />
        </Tarjeta>
        <Tarjeta titulo={translate("nevora.panel_direccion.en_rojo")}>
          {resumen.enRojo.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("nevora.panel_direccion.sin_rojo")}
            </p>
          ) : (
            <ul className="space-y-2">
              {resumen.enRojo.slice(0, 8).map((n) => (
                <li key={String(n.proyecto_id)}>
                  <button
                    type="button"
                    onClick={() => abrirFicha(n.negocio_id, n.proyecto_id)}
                    className="flex w-full items-center gap-2 text-left text-sm hover:underline"
                  >
                    <Semaforo negocio={n} />
                    <span className="truncate flex-1">{n.nombre}</span>
                    <EtiquetaEtapa etapa={n.etapa} />
                  </button>
                </li>
              ))}
              {resumen.enRojo.length > 8 ? (
                <li className="text-xs text-muted-foreground">
                  +{resumen.enRojo.length - 8}
                </li>
              ) : null}
            </ul>
          )}
        </Tarjeta>
        <Tarjeta titulo={translate("nevora.panel_direccion.motivos")}>
          {motivos.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("nevora.panel_direccion.sin_motivos")}
            </p>
          ) : (
            <Barras
              filas={motivos.map(([motivo, v]) => ({
                etiqueta:
                  motivo || translate("nevora.panel_direccion.sin_motivo"),
                valor: v,
              }))}
            />
          )}
        </Tarjeta>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MarcaCabecera />
          <div className="ml-auto flex items-center gap-1">
            <CampanaAvisos plegada alinear="end" />
            <EnlacePerfilMovil />
          </div>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return <div className="mt-2">{cuerpo}</div>;
};

const Cifra = ({
  etiqueta,
  valor,
  enlace,
  alerta,
}: {
  etiqueta: string;
  valor: string;
  enlace?: string;
  alerta?: boolean;
}) => {
  const contenido = (
    <div
      className={cn(
        "rounded-lg border bg-card px-4 py-3 h-full",
        enlace && "hover:bg-muted transition-colors",
      )}
    >
      <p className="etiqueta">{etiqueta}</p>
      <p
        className={cn(
          "text-2xl font-semibold tracking-tight mt-1",
          alerta
            ? "text-red-700 dark:text-red-400"
            : "text-[#336394] dark:text-[#6FA0D6]",
        )}
      >
        {valor}
      </p>
    </div>
  );
  return enlace ? <Link to={enlace}>{contenido}</Link> : contenido;
};

const Tarjeta = ({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-lg border bg-card p-4 space-y-3">
    <h2 className="etiqueta">{titulo}</h2>
    {children}
  </section>
);

const Barras = ({
  filas,
}: {
  filas: { etiqueta: string; valor: number }[];
}) => {
  const max = Math.max(1, ...filas.map((f) => f.valor));
  return (
    <ul className="space-y-1.5">
      {filas.map((f) => (
        <li key={f.etiqueta} className="text-sm">
          <div className="flex justify-between gap-2">
            <span className="truncate">{f.etiqueta}</span>
            <span className="font-medium tabular-nums">{f.valor}</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted mt-0.5">
            <div
              className="h-1.5 rounded-full bg-[#3D8BD4]"
              style={{ width: `${(f.valor / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

const ListaAgenda = ({
  elementos,
  vacio,
}: {
  elementos: ReturnType<typeof elementosAgenda>;
  vacio: string;
}) => {
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  if (!elementos.length) {
    return <p className="text-sm text-muted-foreground">{vacio}</p>;
  }
  return (
    <ul className="space-y-2">
      {elementos.slice(0, 8).map((e) => (
        <li key={e.id}>
          <button
            type="button"
            onClick={() =>
              abrirFicha(e.negocio.negocio_id, e.negocio.proyecto_id)
            }
            className="block w-full text-left text-sm hover:underline"
          >
            <span className="text-xs text-muted-foreground">
              {e.conHora ? formatoFechaHora(e.fecha) : formatoFecha(e.fecha)} ·{" "}
              {translate(`nevora.agenda.${e.tipo}`)}
            </span>
            <span className="block truncate">
              {e.negocio.nombre}
              {e.tipo === "siguiente_paso" && e.titulo !== e.negocio.nombre
                ? ` — ${e.titulo}`
                : ""}
            </span>
          </button>
        </li>
      ))}
      {elementos.length > 8 ? (
        <li className="text-xs text-muted-foreground">
          <Link to="/agenda" className="hover:underline">
            {translate("nevora.panel_direccion.ver_todas")}
          </Link>
        </li>
      ) : null}
    </ul>
  );
};
